import { CompanyFixedAssetSpecies, FixedAssetSpeciesCategory } from '~/types/fixed-asset';

/**
 * True when the category is missing at least one of the bookkeeping accounts
 * (asset, depreciation charge, accumulated depreciation) needed to generate
 * the depreciation entries when registering a fixed asset.
 */
export const hasMissingAccounts = (speciesCategory: FixedAssetSpeciesCategory): boolean =>
  !speciesCategory.assetAccountId ||
  !speciesCategory.depreciationAccountId ||
  !speciesCategory.accumulatedDepreciationAccountId;

const speciesCategoryKey = (speciesId: string, categoryCode: string) => `${speciesId}|${categoryCode}`;

/** Indexes a company's species categories by species id + category code for O(1) lookups. */
export const buildSpeciesCategoryIndex = (
  companySpecies: CompanyFixedAssetSpecies[],
): Map<string, FixedAssetSpeciesCategory> => {
  const index = new Map<string, FixedAssetSpeciesCategory>();
  companySpecies.forEach((cs) =>
    cs.species.speciesCategories?.forEach((sc) => {
      // `category` may be missing when the catalog response didn't hydrate it; skip such rows
      // instead of throwing so the rest of the index (and the form) keeps working.
      if (sc.category == null) return;
      index.set(speciesCategoryKey(cs.species.id, sc.category.code), sc);
    }),
  );
  return index;
};

/** Looks up a species category in an index built by {@link buildSpeciesCategoryIndex}. */
export const findSpeciesCategory = (
  index: Map<string, FixedAssetSpeciesCategory> | undefined,
  speciesId: string | null | undefined,
  categoryCode: string | null | undefined,
): FixedAssetSpeciesCategory | undefined =>
  index && speciesId && categoryCode ? index.get(speciesCategoryKey(speciesId, categoryCode)) : undefined;
